import type { ThemeId } from '@/components/video-editor/explainer/types';

import type { WorkflowType } from '@/constants/video-generation-presets';

/**
 * Video Agent "use-case" presets — landing-page configs reached via the
 * `?template=<slug>` query param on /create-video (e.g.
 * `/create-video?template=real-estate-marketing-video`).
 *
 * A preset pre-selects a workflow (e.g. "Your own media") and swaps the page
 * title / description / SEO so the same Video Agent form doubles as a focused,
 * SEO-friendly landing page for a specific use case. It only seeds defaults —
 * the user can still change anything afterwards.
 *
 * The param is named `template` (not `preset`) on purpose: `preset` already
 * refers to the visual *style* preset (cinematic, anime, …) inside the form.
 */
export interface VideoAgentPreset {
  /** URL slug — matches the `?template=` value. */
  slug: string;
  /** Workflow tab to pre-select. */
  workflowType: WorkflowType;
  /** H1 shown in the page header. */
  title: string;
  /** Sub-header paragraph under the H1. */
  description: string;
  /** SEO <title>. Falls back to `title`. */
  seoTitle?: string;
  /** SEO meta description. Falls back to `description`. */
  seoDescription?: string;
  /** Explainer presets: the look to pick (one the form offers). */
  explainerLook?: ThemeId;
  /** An example idea for the empty idea box (the user can replace it). */
  idea?: string;
}

/**
 * One preset per Explainer look, opened from the look's Get Inspired
 * template (mediaTemplates.ts, category Explainers).
 */
const explainerPreset = (
  slug: string,
  explainerLook: ThemeId,
  title: string,
  description: string,
  idea: string,
): VideoAgentPreset => ({
  slug,
  workflowType: 'explainer',
  explainerLook,
  title,
  description,
  idea,
});

export const VIDEO_AGENT_PRESETS: Record<string, VideoAgentPreset> = {
  'real-estate-marketing-video': {
    slug: 'real-estate-marketing-video',
    workflowType: 'your-own-media',
    title: 'Real Estate Marketing Video',
    description:
      'Create stunning real estate videos automatically with our Real Estate Marketing Video AI. Just upload photos & text — get pro video tours instantly.',
  },
  'comic-explainer-video': explainerPreset(
    'comic-explainer-video',
    'comic',
    'Comic Explainer Video Maker',
    'Describe a topic and get a narrated explainer drawn as bold comic panels, with characters, speech balloons and captions that appear on the word the narrator says them. Up to 10 minutes long.',
    'A short explainer on how coffee gets from the farm to your cup',
  ),
  'riso-print-explainer-video': explainerPreset(
    'riso-print-explainer-video',
    'riso',
    'Riso Print Explainer Video Maker',
    'Turn any topic into a narrated explainer printed in two bright inks on grainy paper, with icons, charts and maps that appear on the word the narrator says them. Up to 10 minutes long.',
    'Why the sky is blue, explained simply',
  ),
  'pixel-art-explainer-video': explainerPreset(
    'pixel-art-explainer-video',
    'pixel',
    'Pixel Art Explainer Video Maker',
    'Make a narrated explainer that plays like a retro 8-bit video game, with levels for chapters and pixel characters and icons that appear on the word the narrator says them. Up to 10 minutes long.',
    'How a computer turns code into a game, for beginners',
  ),
  'stickman-explainer-video': explainerPreset(
    'stickman-explainer-video',
    'stickman',
    'Stickman Explainer Video Maker',
    'Turn any topic into a narrated explainer drawn in black marker on white paper: a hand draws stick figures, line icons and headings on the word the narrator says them. Up to 10 minutes long.',
    'Why we procrastinate, and three small ways to get started',
  ),
  'watercolor-explainer-video': explainerPreset(
    'watercolor-explainer-video',
    'watercolor',
    'Watercolor Storybook Explainer Video Maker',
    'Turn a topic into a narrated storybook explainer: every object and character is painted in watercolor just for your video. Up to 10 minutes long.',
    'How bees turn flower nectar into honey',
  ),
  'clay-animation-explainer-video': explainerPreset(
    'clay-animation-explainer-video',
    'claymation',
    'Claymation Explainer Video Maker',
    'Make a narrated explainer with clay models on a set: every object and character is modeled in clay just for your video. Up to 10 minutes long.',
    'Why starting to save early beats saving more later',
  ),
  'vintage-engraving-explainer-video': explainerPreset(
    'vintage-engraving-explainer-video',
    'engraving',
    'Vintage Engraving Explainer Video Maker',
    'Tell a story in the style of old encyclopedia plates: every object and character is engraved just for your video, with maps and timelines that appear on the word. Up to 10 minutes long.',
    'How the printing press changed the world',
  ),
};

export function getVideoAgentPreset(
  slug: string | string[] | undefined,
): VideoAgentPreset | undefined {
  if (typeof slug !== 'string') return undefined;
  return VIDEO_AGENT_PRESETS[slug];
}
